<template>
    <modals-generic
    :headerColor="headerColor"
    :title="movementDetails.description"
    :subTitle="amountFormat(Math.abs(movementDetails.amount), movementDetails.currency)"
    :subTitleIcon="headerTitleIcon"
    :icon="headerIcon"
    :buttonsTopData="buttonsTopData"
    :showFooter="showFooterHandler"
    :buttonsFooterData="buttonsFooterData"
    :centerLoader="showLoader"
    :triggerClose="triggerClose"
    @hideMe="[emit('hideMe'), cancelAllPendingRequests()]">
        <div class="movement-details" v-if="!showLoader && !applyErrorState">
            <div
            v-for="(lineGroup, index) in parseLineGroups(details)"
            :key="`lineGroup${lineGroup.id}`"
            class="group-wrapper">
                <divider-generic :icon="lineGroup.icon" :title="lineGroup.label" :applyBorder="index !== 0"/>
                <!-- Text Block for each line -->
                <text-block-generic
                v-for="line in lineGroup.lines"
                :key="`lineGroupLine${line,id}`"
                :params="line"
                :icon="line.icon"
                :colorIconPreset="line.colorIconPreset"
                applyEllipsis/>
                <!-- Category Dropdown -->
                <dropdown-category
                v-if="hasCategory && !showLoader && !applyErrorState && index === (parseLineGroups(details).length - 1)"
                class="category-dropdown"
                :params="{
                    labelText: t('accounts.balancesAndMovements.detailModal.category'),
                    placeholder: t('accounts.balancesAndMovements.detailModal.categoryPlaceholder')
                }"
                :options="parsedMovimentCategories"
                :selectedOption="selectedCategory"
                @clicked="selectedCategory = $event"/>
            </div>
        </div>
        <!-- Loader Generic -->
        <loaders-generic v-if="showLoader" fullSize/>
        <!-- Detail Error State -->
        <empty-state-error v-if="applyErrorState && !showLoader" :message="t('accounts.balancesAndMovements.detailModal.movementDetailError')"/>
    </modals-generic>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import ModalsGeneric from '@/components/generic/modals/side/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import DropdownCategory from '@/components/generic/dropdowns/categorys/Category'
import EmptyStateError from '@/components/generic/emptyStates/Error'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue & Helpers */
import { ref, computed, watch } from 'vue'
import { parseLineGroups } from '@/assets/scripts/parsers/genericDetail'
import { categoryIconMapper } from '@/assets/scripts/mappers/categories'
import { amountFormat } from '@/assets/scripts/amounts'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'
import { errorObjectConstructor, sucessObjectConstrutor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { requestMovementDetailDownloadUrl } from '@/network/api/accounts.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { requestMovimentDetail, requestGenericOperationDetail, requestMovementsCategories, updateSelectedCategoryFetch } = mapActions(moduleMapper.ACCOUNTS_BALANCES_AND_MOVEMENTS)
const { getMovementsCategories } = mapGetters(moduleMapper.ACCOUNTS_BALANCES_AND_MOVEMENTS)

/* Emmitters */
const emit = defineEmits(['hideMe', 'showOverlayLoader', 'requestListForCategoryUpdate'])

const props = defineProps({
    movementDetails: Object
})

/* Data */
const applyErrorState = ref(false)
const showLoader = ref(false)
const triggerClose = ref(false)

/*************************************************
*                                                *
*                     Modal                      *
*                                                *
*************************************************/
const details = ref([])

const hasCategory = computed(() => { return props.movementDetails.accountType !== 'DWALLET' })

const headerColor = computed(() => {
    return props.movementDetails.categoryView ? props.movementDetails.categoryView.color : null
})

const headerIcon = computed(() => {
    return props.movementDetails.categoryView && !props.movementDetails.categoryView.name.includes('UNCATEGORIZED') ? categoryIconMapper(props.movementDetails.categoryView.name) : null
})

const headerTitleIcon = computed(() => {
    if (props.movementDetails.amount === 0) return null
    return props.movementDetails.amount > 0 ? 'arrow-up-full' : 'arrow-down-full'
})

const urlPayload = ref({
    accountId: props.movementDetails.accountId,
    movementId: props.movementDetails.id,
    params: {
        releaseDatePosition: props.movementDetails.releaseDatePosition,
        orderNumberPosition: props.movementDetails.orderNumberPosition
    }
})

const buttonsTopData = {
	downloadUrl: requestMovementDetailDownloadUrl(urlPayload.value),
    emailAction: 'sendMovementEmailNotification',
    smsAction: 'sendMovementSMSNotification',
    sendPayload: urlPayload.value,
    operationId: props.movementDetails.sourceSystemView && props.movementDetails.sourceSystemView.operationId ? props.movementDetails.sourceSystemView.operationId : null
}

const buttonsFooterData = [{
	label: t('accounts.balancesAndMovements.detailModal.cancelBtn'),
	buttonPosition: 'left',
	callbackFunction: () => { props.movementDetails.categoryView ? selectedCategory.value = props.movementDetails.categoryView.categoryId : selectedCategory.value = null }
}, {
	label: t('accounts.balancesAndMovements.detailModal.saveBtn'),
	buttonType: 'primary',
    icon: 'check-line',
    iconPosition: 'right',
	callbackFunction: () => { updateCategoryHandler(selectedCategory.value) }
}]

const parsedMovimentCategories = computed(() => {
    const parsedCategories = getMovementsCategories.value.map(category => {
        return {
            id: category.categoryId,
            text: category.translation,
            icon: categoryIconMapper(category.name),
            backgroundColor: category.color?.split('_')[0],
            darkBackgroundColor: category.color?.split('_').length > 1 ? category.color.split('_')[1] : null,
            categoryType: category.categoryType
        }
    })
    const detailCategoryType = props.movementDetails.amount < 0 ? 'EXPENSES' : 'INCOME'

    return parsedCategories.filter(category => {
		return detailCategoryType === category.categoryType
	})
})

const selectedCategory = ref()

const showFooterHandler = computed(() => {
    if (showLoader.value) return

    if (props.movementDetails.categoryView) {
        return selectedCategory.value !== props.movementDetails.categoryView.categoryId
    }

    return selectedCategory.value
})

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestMovementDetails = async () => {
	showLoader.value = true
	showFeedbackToast.value = false
    applyErrorState.value = false

    let payload
    const genericOperationRequest = (props.movementDetails.sourceSystemView && props.movementDetails.sourceSystemView.operationId)

    if (genericOperationRequest) {
        payload = { operationId: props.movementDetails.sourceSystemView.operationId }
    } else {
        payload = {
            accountId: props.movementDetails.accountId,
            movementId: props.movementDetails.id,
            params: {
                releaseDatePosition: props.movementDetails.releaseDatePosition,
                orderNumberPosition: props.movementDetails.orderNumberPosition
            }
        }
    }

	try {
        const response = await (genericOperationRequest ? requestGenericOperationDetail(payload) : requestMovimentDetail(payload))
        if (hasCategory.value) await requestMovementsCategories()
        if (hasCategory.value && props.movementDetails.categoryView) selectedCategory.value = props.movementDetails.categoryView.categoryId
        details.value = response.data.data.lineGroups
        showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
        applyErrorState.value = true
		errorHandler(error.response, requestMovementDetails)
	}
}

watch(() => props.movementDetails, () => {
    if (hasCategory.value && props.movementDetails.categoryView) selectedCategory.value = props.movementDetails.categoryView.categoryId
})

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const updateCategoryHandler = async categoryId => {
	emit('showOverlayLoader', true)

	const categoryPayload = {
		accountId: props.movementDetails.accountId,
		movementId: props.movementDetails.id,
		body: {
			categoryId: categoryId
		},
		queryParams: {
			releaseDatePosition: props.movementDetails.releaseDatePosition,
			orderNumberPosition: props.movementDetails.orderNumberPosition
		}
	}

	try {
		const response = await updateSelectedCategoryFetch(categoryPayload)
        emit('requestListForCategoryUpdate')
		successHandler(response.data)
	} catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, updateCategoryHandler)
	}
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = (data) => {
	feedbackToastData.value = sucessObjectConstrutor(data.message)
	showFeedbackToast.value = true
}

const errorHandler = (data, retryFn) => {
    showLoader.value = false
    emit('showOverlayLoader', false)

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
    showFeedbackToast.value = true
}

requestMovementDetails()
</script>

<style lang="scss" scoped>
.movement-details {
    display: flex;
    flex-direction: column;

    .category-dropdown {
        width: calc(100% - 16px);
    }
}
</style>
